<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-map-marker-check'"
      :section-title="$tc('configuration.sections.municipalities', 2)"
      :section-subtitle="msgSelectedRows"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteMunicipalities(selectedRows)"
        @shortkey="deleteMunicipalities(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showMunicipalityModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showMunicipalityModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showMunicipalityModal($t('configuration.actions.add'))"
        @shortkey="showMunicipalityModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        pagination
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="municipalityModal">
      <municipality-modal
        :action-title="actionTitle"
        @update="patchMunicipalities($event)"
        @create="createMunicipality($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import columnsMunicipalitiesGrid from '@/modules/configurationModule/detail/demography/municipalities/columnsMunicipalitiesGrid'
import MunicipalityModal from '@/modules/configurationModule/detail/demography/municipalities/MunicipalityModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import MunicipalityConfigurationServices from '@/api/resources/configuration/demography/municipality/MunicipalityConfigurationServices'

export default {
    name: 'DemographyMunicipalitiesGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        MunicipalityModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.demography.municipalities',
            actionTitle: null,
            selectedRows: [],
            columnDefs: columnsMunicipalitiesGrid(),
            mode: {
                size: 25
            }
        }
    },
    computed: {
        msgSelectedRows() {
            let msg = `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight
            }
        },
        hasActiveMunicipality() {
            return !!this.selectedRows.find(municipality => municipality.active)
        },
        hasDeactiveMunicipality() {
            return !!this.selectedRows.find(municipality => !municipality.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === (this.selectedRows.filter(municipality => municipality.active).length)
        },
        hasAllDeactivated() {
            return this.selectedRows.length === (this.selectedRows.filter(municipality => !municipality.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActiveMunicipality && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactiveMunicipality && !this.hasAllActivated
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const municipalitiesToPatch = selectionBatch.filter(municipality => municipality.active !== option).map(municipality => {
                return {
                    active: option,
                    id: municipality.id
                }
            })
            if (municipalitiesToPatch.length) {
                const response = await this.$_toCallUnique(MunicipalityConfigurationServices.patchMunicipalitiesBatch(municipalitiesToPatch))
                if (response) {
                    if (municipalitiesToPatch.length > 1) this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: municipalitiesToPatch.length }))
                    else this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
                    this.reloadData()
                }
            }
        },
        async patchMunicipalities(...municipalities) {
            const response = await this.$_toCallUnique(MunicipalityConfigurationServices.patchMunicipalitiesBatch(municipalities))
            if (response) {
                this.$refs.municipalityModal.hide()
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
                this.reloadData()
            }
        },
        async createMunicipality(municipality) {
            const response = await this.$_toCallUnique(MunicipalityConfigurationServices.createMunicipality(municipality))
            if (response) {
                this.$refs.municipalityModal.hide()
                this.$_showToastSuccess(this.$t('configuration.toastMessages.created'))
                this.reloadData()
            }
        },
        async deleteMunicipalitiesBatch(municipalities) {
            const municipalitiesToDelete = municipalities.map(municipality => municipality.id)
            const response = await this.$_toCallUnique(MunicipalityConfigurationServices.deleteMunicipalitiesBatch(municipalitiesToDelete))
            if (response) {
                if (municipalitiesToDelete.length > 1) this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 2, { elements: municipalitiesToDelete.length }))
                else this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
                this.reloadData()
            }
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(MunicipalityConfigurationServices))
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () => this.showMunicipalityModal(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.$t('configuration.actions.delete'),
                        cssClasses: this.selectedRows.length < 1 ? ['not-show'] : '',
                        action: () => this.deleteMunicipalities(this.selectedRows)
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    }
                ]
            }
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(MunicipalityConfigurationServices))
        },
        showMunicipalityModal(title, selectedMunicipality) {
            this.actionTitle = title
            this.$refs.municipalityModal.show([selectedMunicipality])
        },
        async deleteMunicipalities(municipalities) {
            if (municipalities.length) {
                const promise = await this.$_showConfirmationModal(`${this.$t('configuration.grid.deleteConfirmation')} ${this.selectedRows.length} ${this.$tc('configuration.grid.element', this.selectedRows.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deleteMunicipalitiesBatch(municipalities)
                }
            }
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        }
    }
}
</script>
